import { ElementRef, Injectable } from '@angular/core';

import irisDesignTokens from '@sondermind/iris-design-tokens/design-tokens.json';

@Injectable({
  providedIn: 'root'
})
export class IrisTokenParserService {
  private buildCamelCaseFromArray(array: string[]): string {
    return array.reduce((prev, curr, index) => {
      const accumulator = prev + (index === 0
        ? curr.toLocaleLowerCase()
        : curr.charAt(0).toUpperCase() + curr.slice(1));
      return accumulator;
    }, '');
  }

  /**
   * Translates the design token SCSS name to a key that can be used
   * to retrieve the token value from the token JSON file.
   * Turns '$color-dec-turquoise-03' into 'colorDecTurquoise03'
   * @param sassToken The name of the Iris design token as it would usually be used in SCSS
  */
  parseDesignTokenJsonKey(sassToken: string): string {
    const noDollarSign = sassToken.slice(1);
    const wordArray = noDollarSign.split('-');

    return this.buildCamelCaseFromArray(wordArray);
  }

  /**
   * Sets a CSS custom property on the given element to the value defined by the design token.
   * The style property should be defined in the scss and named something with the format `--{tokenName}`.
   * We can then access the value of that custom property using `var(--{styleName})` in the element's scss.
   * @param token The name of the Iris design token as it would be used in SCSS eg. '$color-dec-turquoise-03'
   * @param styleName The name of the CSS custom property (without the leading --)
   * @param elementRef The element to set the style on
   */
  setStyleFromToken(token: string, styleName: string, elementRef: ElementRef): void {
    if (!elementRef) {
      // eslint-disable-next-line no-console
      console.error(`Given element is not defined. Cannot apply desired style for ${styleName}.`);
      return;
    }

    if (token.startsWith('$')) {
      const jsonKey = this.parseDesignTokenJsonKey(token);
      const tokenValue = (irisDesignTokens as { [key: string]: string; })[jsonKey];
      if (tokenValue) {
        elementRef.nativeElement.style.setProperty(`--${styleName}`, tokenValue);
      } else {
        // eslint-disable-next-line no-console
        console.error(`Iris token parser error for ${styleName}: ${token} is not a recognized Iris design token.`);
      }
    }
  }

  setColorFromToken(colorToken: string, tokenName: string, elementRef: ElementRef): void {
    this.setStyleFromToken(colorToken, `${tokenName}-color`, elementRef);
  }

  getColorFromToken(token: string): string {
    if (token.startsWith('$')) {
      const jsonKey = this.parseDesignTokenJsonKey(token);
      return (irisDesignTokens as { [key: string]: string; })[jsonKey] ?? 'red';
    }
    return 'red';
  }
}
